import {ReduceStore} from "flux/utils";
import dotProp from "dot-prop-immutable";
import Dispatcher from "Dispatcher";
import PropTypes from "prop-types";
import DefineActions from "jsmodel/DefineActions";
import get_type_key from "jsmodel/get_type_key";
import {auditable} from "audit";
import HybridConfig from "lib/HybridConfig";
import typeCheckingStore from "data/util/typeCheckingStore";
import humanizedKeyList from "data/util/humanizedKeyList";

const COLLISION_KEY = "_collision_";

const buildInitialColumns = (info) => {
    const key = info.key;
    const columns = {
        byKey: {},
        idxName: {},
    };
    for (const c of info.columns) {
        const col = {
            ...c,
            type: get_type_key(c.type),
        };
        if (columns.idxName.hasOwnProperty(c.name)) {
            const curr = columns.idxName[c.name];
            if (c.name !== "ao_id") console.log(`'${key}.${c.key}' collides on the '${c.name}' name ` + (curr === COLLISION_KEY ? "too" : `w/ '${curr}'`));
            // so it can't be used ambiguously
            columns.idxName[c.name] = COLLISION_KEY;
        } else {
            columns.idxName[c.name] = c.key;
        }
        if (columns.byKey.hasOwnProperty(c.key)) {
            throw new Error(`Collision on ${key}'s ${c.key} key`);
        }
        columns.byKey[c.key] = col;
    }
    return columns;
};

const addDerivationsToColumns = (info, columns) => {
    // derivations get reversed (so they're like downstreams), but we can't do
    // it inline above, as the source field might be later in the column list.
    for (const c of info.columns) {
        const derivation = c.derivation;
        if (derivation == null) continue;

        derivation.targetKey = c.key;
        derivation.targetName = c.name;
        // eslint-disable-next-line no-new-func
        derivation.script = new Function("instance", derivation.script);
        if (HybridConfig.auditScriptlets()) {
            derivation.script = auditable("derivation", derivation.script);
        }

        if (derivation.sourceKey == null) continue;

        const src = columns.byKey[derivation.sourceKey];
        if (! src.hasOwnProperty("derivations")) {
            src.derivations = [];
        }
        src.derivations.push(derivation);
    }
};


const addDownstreamsToColumnQueue = (queue, key, c, type) => {
    for (const ds of c.downstreams[type]) {
        const parts = ds.split(".");
        if (parts.length === 1) continue; // no edges
        queue.push({
            tableKey: key,
            triggerCol: parts[0],
            type,
            parts,
        });
    }
};

const processColumnQueueState = (state, queue) => {
    const newQueue = [];
    while(queue.length > 0) {
        const {
            tableKey,
            triggerCol,
            parts,
            type,
        } = queue.shift();
        if (parts.length === 0) {
            throw new Error("No parts, eh?");
        }
        if (! state.tables.hasOwnProperty(tableKey)) {
            newQueue.push({
                tableKey,
                triggerCol,
                parts,
                type,
            });
            continue;
        }
        const info = state.tables[tableKey].byKey[triggerCol];
        if (info == null || ! info.hasOwnProperty("name")) {
            throw new Error(`No ${tableKey}.${triggerCol} is known with ${parts} of ${type}?!`);
        }
        if (info.inverse) {
            // add the downstream on the inverse for symmetry/sanity, even
            // though only the real/forward association will ever have its
            // downstreams processed.
            state = addDownstream(state, info, tableKey, triggerCol, type, parts);
            queue.push({
                tableKey: info.otherTableKey,
                triggerCol: info.inverseKey,
                parts: parts[0] === info.key ? parts.slice(1) : parts,
                type,
            });
            continue;
        }
        if (parts.length === 1) {
            if (parts[0] !== triggerCol) {
                state = addDownstream(state, info, tableKey, triggerCol, type, parts);
            }
            continue; // no more edges
        }

        state = addDownstream(state, info, tableKey, triggerCol, type, parts);

        // cascade on to subsequent edges...
        if (triggerCol === parts[0]) {
            queue.push({
                tableKey: info.otherTableKey,
                triggerCol: parts[1],
                parts: parts.slice(1),
                type,
            });
        } else {
            queue.push({
                tableKey,
                triggerCol: parts[0],
                parts,
                type,
            });
        }
    }
    state = dotProp.set(state, "downstreamQueue", newQueue);
    return state;
};

export const loadTableIntoState = (state, info) => {
    const key = info.key;
    const columns = buildInitialColumns(info);
    addDerivationsToColumns(info, columns);

    // this table's columns are set
    state = dotProp.set(state, ["tables", key], columns);

    // Downstreams need to be split up and the remainder added to each
    // intervening edge's downstreams. Derivations would need this too, except
    // they can't traverse assocs, so there is always zero intervening edges.
    //
    // Because we load tables one at a time, those edges might go through tables
    // which aren't defined yet, and thus can't be inflated. So we queue the
    // work, and then flush the queue. About half the time, the newly queued
    // work will NOT be flushed. The other half of the time some previously
    // queued work will ALSO be flushed.
    const queue = state.downstreamQueue.slice();
    for (const c of Object.values(columns.byKey)) {
        if (c.downstreams == null) continue;
        for (const type of Object.keys(c.downstreams)) {
            if (type === "VALIDATION") continue;
            addDownstreamsToColumnQueue(queue, key, c, type);
        }
    }

    // now process
    if (queue.length > 0) {
        state = processColumnQueueState(state, queue);
    }

    return state;
};

const addDownstream = (state, info, tableKey, triggerCol, type, parts) => {
    // add to this edge's downstreams...
    if (! info.hasOwnProperty("downstreams")) {
        info.downstreams = {};
    }
    if (! info.downstreams.hasOwnProperty(type)) {
        info.downstreams[type] = [];
    }
    const ds = parts.join(".");
    if (info.downstreams[type].indexOf(ds) < 0) {
        state = dotProp.set(
            state,
            ["tables", tableKey, "byKey", triggerCol],
            dotProp.set(
                info,
                ["downstreams", type],
                dss => dss.concat(ds),
            ),
        );
    }

    return state;
};

class TableColumnMetaStore extends ReduceStore {

    getInitialState() {
        return {
            tables: {},
            downstreamQueue: [],
        };
    }

    reduce(state, action) {
        if (action.type === DefineActions._UNLOAD) {
            return this.getInitialState();
        }
        if (action.type === DefineActions.TABLE) {
            return loadTableIntoState(state, action.def);
        }
        return state;
    }

    getAssociationFieldKeys(tableKey) {
        const assocs = [];
        const columns = this.getState().tables[tableKey];
        if (! columns) {
            return assocs;
        }
        for (const fk of Object.keys(columns)) {
            const f = columns[fk];
            if (f.type === "ASSOCIATION") {
                assocs.push(fk);
            }
        }
        return assocs;
    }

    _getIndexes(tableKey) {
        const s = this.getState().tables;
        if (!s.hasOwnProperty(tableKey)) {
            throw new Error(`No '${tableKey}' table is known: ${Object.keys(s)}`);
        }
        return s[tableKey];
    }

    // deprecated
    getInfo(tableKey, columnName) {
        console.warn("TableColumnMetaStore.getInfo is deprecated; use getInfoByName");
        return this.getInfoByName(tableKey, columnName);
    }

    getInfoByName(tableKey, columnName) {
        const columns = this._getIndexes(tableKey).idxName;
        if (! columns.hasOwnProperty(columnName)) {
            throw new Error(`No '${columnName}' column is known on '${tableKey}': ${Object.keys(columns)}`);
        }
        const key = columns[columnName];
        if (key === COLLISION_KEY) {
            throw new Error(`Column '${columnName}' on '${tableKey}' has a naming collision (check your inverses)`);
        }
        return this.getInfoByKey(tableKey, key);
    }

    getAllColumnsByName(tableKey) {
        const result = {};
        const idxs = this._getIndexes(tableKey);
        for (const name of Object.keys(idxs.idxName)) {
            const key = idxs.idxName[name];
            if (key === COLLISION_KEY) {
                continue;
            }
            result[name] = idxs.byKey[key];
        }
        return result;
    }

    getAllColumns(tableKey) {
        return Object.values(this._getIndexes(tableKey).byKey);
    }

    // this is gross, but needed because of how we started with names
    getInfoByKey(tableKey, columnKey) {
        const columns = this._getIndexes(tableKey).byKey;
        if (!columns.hasOwnProperty(columnKey)) {
            throw new Error(process.env.NODE_ENV === "development"
                ? `No '${columnKey}' column is known on '${tableKey}': ${humanizedKeyList(columns)}`
                : `No '${columnKey}' column is known on '${tableKey}'`);
        }
        return columns[columnKey];
    }
}

const metaType = PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    downstreams: PropTypes.shape({
        ENABLEMENT: PropTypes.arrayOf(PropTypes.string),
        ASSIGNMENT: PropTypes.arrayOf(PropTypes.string),
        VALIDATION: PropTypes.arrayOf(PropTypes.string),
        CANDIDATE_FILTER: PropTypes.arrayOf(PropTypes.string),
    }),
});
TableColumnMetaStore.stateTypes = {
    downstreamQueue: PropTypes.arrayOf(
        PropTypes.shape({
            tableKey: PropTypes.string.isRequired,
            triggerCol: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            parts: PropTypes.arrayOf(PropTypes.string).isRequired,
        })
    ).isRequired,
    tables: PropTypes.objectOf(
        PropTypes.shape({
            idxName: PropTypes.objectOf(PropTypes.string).isRequired,
            byKey: PropTypes.objectOf(metaType).isRequired,
        })
    ).isRequired,
};

export default typeCheckingStore(new TableColumnMetaStore(Dispatcher));



// WEBPACK FOOTER //
// ./app/data/meta/TableColumnMetaStore.js